import { posthog } from 'posthog-js';
import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { userStore } from '~/lib/stores/user';

export const usePosthogUserProperties = () => {
  const userData = useStore(userStore);

  useEffect(() => {
    if (userData) {
      // Set user properties
      posthog.people.set({
        email: userData.email,
        is_subscribed: userData.isSubscribed,
        prompts_used: userData.promptsUsed,
        max_prompts: userData.maxPrompts,
        plan_name: userData.planName,
        $initial_referrer: document.referrer,
        $initial_referring_domain: document.referrer ? new URL(document.referrer).hostname : null,
      });

      // Set computed properties
      posthog.people.set({
        prompts_remaining: Number(userData.maxPrompts) - Number(userData.promptsUsed),
        usage_percentage: (Number(userData.promptsUsed) / Number(userData.maxPrompts)) * 100,
      });

      // Track retained users (free tier) once they consume all available prompts
      try {
        const promptsUsed = Number(userData.promptsUsed ?? 0);
        const maxPrompts = Number(userData.maxPrompts ?? 0);
        const isFreeTierUser = !userData.isSubscribed;
        const hasReachedLimit = maxPrompts > 0 && promptsUsed >= maxPrompts;

        // Use a person property guard so we only emit this once per user
        const alreadyRetained = !!posthog.get_property('retained_at');

        if (isFreeTierUser && hasReachedLimit && !alreadyRetained) {
          posthog.capture('retained', {
            prompts_used: promptsUsed,
            max_prompts: maxPrompts,
          });

          // Mark the time this user became retained
          posthog.people.set({
            retained_at: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('[PostHog] Failed to track retained user:', error);
      }
    }
  }, [userData]);

  const updateUserProperty = (key: string, value: any) => {
    posthog.people.set({ [key]: value });
  };

  const incrementUserProperty = (key: string, value: number = 1) => {
    const currentValue = Number(posthog.get_property(key) || 0);
    posthog.people.set({ [key]: currentValue + value });
  };

  const appendToUserProperty = (key: string, value: any) => {
    const currentValue = posthog.get_property(key) || [];

    if (Array.isArray(currentValue)) {
      posthog.people.set({ [key]: [...currentValue, value] });
    } else {
      posthog.people.set({ [key]: [value] });
    }
  };

  return {
    updateUserProperty,
    incrementUserProperty,
    appendToUserProperty,
  };
};

export default usePosthogUserProperties;
